import { Button, DialogActions, DialogContent, DialogTitle, Modal, ModalDialog, Table, Typography } from "@mui/joy";
import React from "react";

interface AssignmentHistoryRecord {
	assignment_history_id: string;
	packing_list_id: string;
	reassignment_date: string;
	notes: string | null;
	previous_mechanic_name: string | null;
	new_mechanic_name: string;
	reassigned_by_name: string;
}

interface AssignmentHistoryDialogProps {
	open: boolean;
	packingListId: string;
	invoiceNumber: string;
	onClose: () => void;
}

/**
 * Dialog component for displaying the assignment history of a packing list
 * Shows who was assigned, when, and by whom
 */
export default function AssignmentHistoryDialog(props: AssignmentHistoryDialogProps) {
	const [history, setHistory] = React.useState<Array<AssignmentHistoryRecord>>([]);
	const [isLoading, setIsLoading] = React.useState(false);

	// Fetch history when dialog opens
	React.useEffect(() => {
		if (props.open && props.packingListId) {
			getHistory();
		}
	}, [props.open, props.packingListId]);

	/**
	 * Fetches the assignment history from the API
	 */
	const getHistory = () => {
		setIsLoading(true);
		fetch("PackingList/GetPackingListAssignmentHistory", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packingListId: props.packingListId,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				setHistory(x);
				setIsLoading(false);
			});
	};

	/**
	 * Formats a date string for display
	 */
	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleString();
	};

	return (
		<Modal open={props.open}>
			<ModalDialog sx={{ width: 800, maxHeight: "80vh", overflow: "auto" }}>
				<DialogTitle>Assignment History - {props.invoiceNumber}</DialogTitle>
				<DialogContent>
					{isLoading ? (
						<Typography>Loading...</Typography>
					) : history.length === 0 ? (
						<Typography level="body-sm">No reassignment history found.</Typography>
					) : (
						<Table stripe="odd" size="sm">
							<thead>
								<tr>
									<th>Date</th>
									<th>Previous Mechanic</th>
									<th>New Mechanic</th>
									<th>Reassigned By</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								{history.map((record) => (
									<tr key={record.assignment_history_id}>
										<td>{formatDate(record.reassignment_date)}</td>
										<td>{record.previous_mechanic_name || "N/A"}</td>
										<td>{record.new_mechanic_name}</td>
										<td>{record.reassigned_by_name}</td>
										<td>{record.notes || "-"}</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={props.onClose} color="primary">
						Close
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
